import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { useCart } from '../../lib/cartContext';
import { alsoBoughtApi } from '../../lib/api';

function AlsoBoughtCard({ product }) {
  const { addItem, loading } = useCart();
  const [adding, setAdding] = React.useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    await addItem(product.id);
    setAdding(false);
  };

  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;

  return (
    <Link
      to={`/shop/${product.slug}`}
      className="flex-shrink-0 w-[200px] group block no-underline"
      data-testid={`also-bought-${product.slug}`}
    >
      <div className="relative aspect-square overflow-hidden bg-zinc-900 mb-3">
        {product.images?.[0] ? (
          <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">No Image</div>
        )}
        {hasDiscount && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-[#E63946] text-white">
            Sale
          </span>
        )}
        <button
          onClick={handleAdd}
          disabled={loading || adding}
          className="absolute bottom-2 right-2 w-8 h-8 bg-[#E63946] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-[#c52f3b] disabled:opacity-50"
          data-testid={`also-bought-add-${product.slug}`}
          aria-label="Add to cart"
        >
          {adding ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
        </button>
      </div>
      <p className="text-zinc-500 text-[10px] uppercase tracking-wider">{product.category}</p>
      <h4 className="text-white text-sm mt-0.5 truncate group-hover:text-[#E63946] transition-colors duration-300">
        {product.name}
      </h4>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[#E63946] text-sm font-semibold">${product.price?.toFixed(2)}</span>
        {hasDiscount && (
          <span className="text-zinc-600 line-through text-xs">${product.compare_at_price.toFixed(2)}</span>
        )}
      </div>
    </Link>
  );
}

export default function AlsoBoughtSection({ productId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    alsoBoughtApi.get(productId).then(res => {
      if (!cancelled) setProducts(res.data.products || []);
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId]);

  if (loading) return null;
  if (products.length === 0) return null;

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 220, behavior: 'smooth' });
  };

  return (
    <section className="py-12 border-t border-zinc-800" data-testid="also-bought-section">
      <div className="container-ninja">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[#E63946] text-xs uppercase tracking-wider font-heading mb-1">Complete the Look</p>
            <h3 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Customers Also Bought</h3>
          </div>
          {products.length > 4 && (
            <div className="flex gap-2">
              <button onClick={() => scroll(-1)} className="w-9 h-9 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors" aria-label="Scroll left" data-testid="also-bought-scroll-left">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => scroll(1)} className="w-9 h-9 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors" aria-label="Scroll right" data-testid="also-bought-scroll-right">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map(product => (
            <AlsoBoughtCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
